/**
 * Google-supported account currencies (the billing currencies of Leela-supported countries).
 * Must stay in sync with the backend `CurrencyCode` enum (catalog-config.dto.ts).
 *
 * Options are built once at module load. Label = "USD - US Dollar ($)" using Intl for the display
 * name + symbol, so we don't hand-maintain 34 names/symbols. `currencySymbol()` powers budget/bid
 * input prefixes and read-only display.
 */
export const GOOGLE_SUPPORTED_CURRENCIES = [
  'USD', 'EUR', 'GBP', 'CAD', 'AUD', 'CHF', 'JPY', 'INR', 'BRL', 'MXN', 'AED', 'SAR', 'ZAR', 'SGD',
  'MYR', 'IDR', 'PHP', 'THB', 'TRY', 'PLN', 'CZK', 'HUF', 'RON', 'NOK', 'SEK', 'DKK', 'NZD', 'NGN',
  'EGP', 'ARS', 'CLP', 'COP', 'PEN', 'BOB',
] as const;

export type SupportedCurrency = typeof GOOGLE_SUPPORTED_CURRENCIES[number];

// Exported for unit testing (normal + malformed-code catch fallback). Intl throws a RangeError for a
// malformed currency code (e.g. wrong length) → we fall back to the raw code.
export function symbolOf (code: string): string {
  try {
    const parts = new Intl.NumberFormat('en', { style: 'currency', currency: code }).formatToParts(0);
    return parts.find(p => p.type === 'currency')?.value ?? code;
  } catch {
    return code;
  }
}

export function nameOf (code: string): string {
  try {
    return new Intl.DisplayNames(['en'], { type: 'currency' }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** Currency symbol/icon for a code (falls back to the code). Used for budget/bid input prefixes. */
export function currencySymbol (code?: string): string {
  return code ? symbolOf(code) : '';
}

/**
 * Default daily-budget minimum per currency (major units) — the ~5-USD-equivalent Google DemandGen
 * floor. Shown only as HELPER TEXT next to the account-level minDailyBudget field at onboarding; the
 * field is left blank unless the user overrides, and the backend applies this default when unset.
 * Keep in sync with the backend currency-metadata table (USD/CAD/BRL are Google-verified exact; the
 * rest are buffered-FX estimates). Google enforces the real floor at setup.
 */
const CURRENCY_MIN_DEFAULTS: Record<string, number> = {
  USD: 5,
  EUR: 4.4,
  GBP: 3.8,
  CAD: 7,
  AUD: 7.1,
  CHF: 4.2,
  JPY: 790,
  INR: 480,
  BRL: 25.4,
  MXN: 87,
  AED: 19,
  SAR: 19,
  ZAR: 82,
  SGD: 6.4,
  MYR: 21,
  IDR: 89200,
  PHP: 310,
  THB: 170,
  TRY: 240,
  PLN: 19,
  CZK: 110,
  HUF: 1600,
  RON: 23,
  NOK: 48,
  SEK: 50,
  DKK: 33,
  NZD: 8.8,
  NGN: 6700,
  EGP: 260,
  ARS: 7600,
  CLP: 4800,
  COP: 15900,
  PEN: 17,
  BOB: 51,
};

/** Default daily-budget minimum for a currency (undefined if unknown). */
export function currencyMinDefault (code?: string): number | undefined {
  return code ? CURRENCY_MIN_DEFAULTS[code] : undefined;
}

interface CurrencyOption { label: string; value: string }

const OPTIONS: CurrencyOption[] = GOOGLE_SUPPORTED_CURRENCIES.map(code => ({
  value: code,
  label: `${code} - ${nameOf(code)} (${symbolOf(code)})`,
}));

/** { label, value } options for the account-currency dropdown. */
export function getCurrencyOptions (): CurrencyOption[] {
  return OPTIONS;
}
